import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Nav,
  Row,
  Tab,
} from "react-bootstrap";
import { EditorState, Modifier, SelectionState } from "draft-js";
import MyIcon from "../Common/MyIcon";
import AiIcon from "../../assets/icons/Icon_Library/application icons/library/boomAI_icon.svg2";
import TickIcon from "../../assets/icons/Icon_Library/application icons/general/tick.svg2";
import CloseIcon from "../../assets/icons/Icon_Library/application icons/general/close.svg2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faRotateLeft,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import HelpButton from "../HelpContent/HelpButton";
import EngineerVisitIcon from "../../assets/icons/Icon_Library/application icons/general/engineer_visit.svg2";
import TranslateIcon from "../../assets/icons/Icon_Library/application icons/library/icon_translate.svg2";
import GroupIcon from "../../assets/icons/Icon_Library/application icons/general/message_recipients.svg2";
import AiDropdown from "./AiDropdown";
import { usePostAiPromptMutation } from "../store/slices/settingsSlice";

function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

const translateOptions = [
  { name: "", info: "Do not translate" },
  { name: "gr", info: "German", id: "DE" },
  { name: "fr", info: "French", id: "FR" },
  { name: "es", info: "Spanish", id: "ES" },
  { name: "it", info: "Italian", id: "IT" },
  { name: "pt", info: "Portuguese", id: "PT" },
  { name: "zh", info: "Chinese", id: "CN" },
  { name: "ja", info: "Japanese", id: "JP" },
  { name: "ru", info: "Russian", id: "RU" },
  { name: "ar", info: "Arabic", id: "SA" },
  { name: "tr", info: "Turkish", id: "TR" },
];

export default function AiModal({ editorState, setEditorState }) {
  const [replacements, setReplacements] = useState({});
  const [isOnline, setIsOnline] = useState(false);

  const currentMessage = editorState
    .getCurrentContent()
    .getBlockMap()
    .reduce((acc, b) => ({ ...acc, [b.getKey()]: b.getText() }), {});

  // Collect the display text of all token entities so we can instruct AI to preserve them
  const tokenTexts = (() => {
    const content = editorState.getCurrentContent();
    const texts = [];
    content.getBlocksAsArray().forEach((block) => {
      block.findEntityRanges(
        (char) => char.getEntity() !== null,
        (start, end) => {
          const text = block.getText().slice(start, end);
          if (text && !texts.includes(text)) texts.push(text);
        }
      );
    });
    return texts;
  })();

  const tokenInstruction =
    tokenTexts.length > 0
      ? ` IMPORTANT: the following text segments are template tokens and must be preserved exactly as-is in your response, do not modify, translate, or paraphrase them: ${tokenTexts
          .map((t) => `"${t}"`)
          .join(", ")}.`
      : "";
  // .map((text, index) => index + 1 + " " + text)
  // .join("\n");

  const [aiType, setAiType] = useState();
  const [translate, setTranslate] = useState("");
  const [words, setWords] = useState("");
  const [selected, setSelected] = useState();
  const [getAiResponse] = usePostAiPromptMutation();
  const [grammarStyle, setGrammarStyle] = useState("formal");
  const [loading, setLoading] = useState(false);

  const [correctSpelling, setCorrectSpelling] = useState({});
  const [correctGrammar, setCorrectGrammar] = useState({});
  const [translation, setTranslation] = useState({});
  const [aiErrorMessage, setAiErrorMessage] = useState("");
  const [aiModel, setAiModel] = useState("gpt-5.1-chat-latest");
  const [aiPrompt, setAiPrompt] = useState("");

  const handleReset = () => {
    setCorrectSpelling({});
    setCorrectGrammar({});
    setTranslation({});
    setAiErrorMessage("");
    setReplacements({});
  };

  const handleClose = () => {
    handleReset();
    setAiPrompt("");
    setAiType(undefined);
  };

  const getSuggestions = async (
    suggestionType,
    message,
    p_grammarStyle,
    p_translate,
    p_aiPrompt
  ) => {
    handleReset();
    let prompt = "";
    if (p_aiPrompt && p_aiPrompt.length > 0) {
      prompt = p_aiPrompt;
    } else {
      if (suggestionType === "Spelling" && !!message?.length) {
        prompt = `here is an object with block keys mapped to the block content in quotation marks: ***${message}***
          check the spelling of each block content and identify the words spelt wrong.
          return a json object with block keys mapped to objects with starting index of each mistake
          mapped to an object containing the original and correct spelling. 
          If the block has no mistakes, do not include it in the response.
          If the message does not look like proper text, return a string saying Input does not look like a proper text.
          If there are no mistakes return an empty object.${tokenInstruction}`;
      }
      if (suggestionType === "Content-Grammar" && !!message?.length) {
        prompt = `check the grammar of sentences in each block and return three suggestions for each sentence
       in ${p_grammarStyle}: ***${message}***. 
       return a json object with block keys mapped to objects containing starting indices of each sentence mapped
       to an object containing the original and an array of new sentences.${tokenInstruction}`;
      }
      if (suggestionType === "Language" && !!message?.length) {
        prompt = `here is an object with block keys mapped to the block content in quotation marks: ***${message}***.
        Translate the text into ${p_translate.info} by providing three alternatives.
        return a json object with block keys mapped to objects with original and array of suggestions: ${message}${tokenInstruction}`;
      }
    }

    setLoading(true);
    const res = await getAiResponse({
      prompt,
      type: suggestionType.toLowerCase(),
      model: aiModel,
    });
    if (res?.data) {
      setIsOnline(true);
      const answer = res.data.answers[0];
      // .replace("```json", "")
      // .replace("```", "");
      if (isJsonString(answer) && typeof JSON.parse(answer) === "object") {
        if (suggestionType === "Content-Grammar") {
          setCorrectGrammar(JSON.parse(answer));
        }
        if (suggestionType === "Spelling") {
          setCorrectSpelling(JSON.parse(answer));
        }
        if (suggestionType === "Language") {
          setTranslation(JSON.parse(answer));
        }
      } else {
        setAiErrorMessage(answer);
      }
    } else {
      setIsOnline(false);
    }

    setLoading(false);
  };

  const onReplace = () => {
    let contentState = editorState.getCurrentContent();
    Object.entries(replacements).forEach(([blockKey, value]) => {
      let offset = 0;
      Object.entries(value).forEach(([start, suggestion]) => {
        if (!suggestion) return;
        const anchorOffset = parseInt(start) + offset;
        const focusOffset = anchorOffset + suggestion.original.length;

        // Collect entity ranges fully within the replacement range so they can be re-applied after
        const block = contentState.getBlockForKey(blockKey);
        const entityRanges = [];
        block.findEntityRanges(
          (character) => character.getEntity() !== null,
          (entityStart, entityEnd) => {
            if (entityStart >= anchorOffset && entityEnd <= focusOffset) {
              entityRanges.push({
                text: block.getText().slice(entityStart, entityEnd),
                entityKey: block.getEntityAt(entityStart),
              });
            }
          }
        );

        // Replace the text
        const blockSelection = SelectionState.createEmpty(blockKey).merge({
          anchorOffset,
          focusOffset,
        });
        contentState = Modifier.replaceText(
          contentState,
          blockSelection,
          suggestion.correct
        );

        // Re-apply entities by finding their text in the replacement string
        entityRanges.forEach(({ text, entityKey }) => {
          const newPos = suggestion.correct.indexOf(text);
          if (newPos === -1) return;
          const entitySelection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: anchorOffset + newPos,
            focusOffset: anchorOffset + newPos + text.length,
          });
          contentState = Modifier.applyEntity(
            contentState,
            entitySelection,
            entityKey
          );
        });

        offset += suggestion.correct.length - suggestion.original.length;
      });
    });

    setEditorState(EditorState.push(editorState, contentState));
    handleClose();
  };

  useEffect(() => {
    if (!aiType || (aiType.heading === "Language" && !translate)) return;
    getSuggestions(
      aiType?.heading,
      JSON.stringify(currentMessage),
      grammarStyle,
      translate
    );
  }, [
    aiType?.heading,
    grammarStyle,
    translate,
    JSON.stringify(currentMessage),
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getSuggestions("Spelling", JSON.stringify(currentMessage));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [JSON.stringify(currentMessage)]);

  const numberOfSpellingErrors = Object.values(correctSpelling)
    ?.map((block) => Object.values(block))
    .flat().length;

  return (
    <div>
      <AiDropdown
        aiType={aiType}
        setAiType={setAiType}
        spellingErrors={numberOfSpellingErrors}
        isOnline={isOnline}
      />

      <Modal
        show={["Spelling", "Content-Grammar", "Language"].includes(
          aiType?.heading
        )}
        onHide={handleClose}
        size="xl"
      >
        <div className="p-4 bg-purple-gradient rounded-3 ">
          <div className="d-flex align-items-center mb-3">
            <div className="d-flex align-items-center position-relative">
              <MyIcon Icon={AiIcon} width={25} />
              <span className="ms-2 text-purple fw-bold">boomAI:</span>
              <span className="ms-2">
                {aiType?.heading === "Spelling" && "Spelling Recommendations"}
                {aiType?.heading === "Content-Grammar" &&
                  "Content and Grammar Recommendations"}
                {aiType?.heading === "Language" && "Change the language"}
              </span>
              {aiType?.heading === "Spelling" && numberOfSpellingErrors ? (
                <span className="ms-2 badge bg-colored text-white rounded-pill position-absolute top-0 start-100 translate-middle">
                  {numberOfSpellingErrors}
                </span>
              ) : null}
            </div>
            <div className="ms-auto cursor-pointer">
              <button
                className="btn"
                onClick={() => {
                  getSuggestions(
                    aiType?.heading,
                    JSON.stringify(currentMessage),
                    grammarStyle,
                    translate
                  );
                }}
              >
                <FontAwesomeIcon icon={faRotateLeft} className="text-purple" />
              </button>
              <button className="btn" onClick={handleClose}>
                <FontAwesomeIcon icon={faTimes} className="text-purple" />
              </button>
            </div>
          </div>
          <hr className="my-2 text-secondary" />

          {aiType?.heading === "Spelling" && (
            <div>
              <div className="row">
                <div className="col-6">
                  <h5>Your Text</h5>
                  <div
                    style={{ minHeight: 200, maxHeight: 400 }}
                    className="bg-white p-3 rounded-3 overflow-auto"
                  >
                    {Object.values(currentMessage).map((text, index) => (
                      <p key={index}>{text}</p>
                    ))}
                  </div>
                </div>
                <div className="col-6">
                  <h5>AI Suggestions</h5>
                  {loading ? (
                    <div
                      style={{ minHeight: 100 }}
                      className="d-flex justify-content-center align-items-center"
                    >
                      <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                  ) : correctSpelling &&
                    Object.entries(correctSpelling).length > 0 ? (
                    Object.entries(correctSpelling).map(([blockKey, value]) =>
                      Object.entries(value).map(
                        ([start, suggestion], index) => (
                          <div key={index} className="mb-2">
                            {suggestion.original}
                            =&gt; {suggestion.correct}
                            <Form.Check
                              type="checkbox"
                              id={
                                "replace" +
                                "-" +
                                start +
                                "-" +
                                suggestion.correct
                              }
                              className="ms-2 d-inline-block align-baseline"
                              label="Replace"
                              checked={
                                replacements[blockKey] &&
                                replacements[blockKey]?.[start]?.correct ===
                                  suggestion.correct
                              }
                              onChange={(e) => {
                                setReplacements((prev) => ({
                                  ...prev,
                                  [blockKey]: {
                                    ...(prev[blockKey] || {}),
                                    [start]: e.target.checked
                                      ? suggestion
                                      : undefined,
                                  },
                                }));
                              }}
                            />
                          </div>
                        )
                      )
                    )
                  ) : (
                    "No spelling mistakes found."
                  )}
                </div>
              </div>
            </div>
          )}

          {["Content-Grammar"].includes(aiType?.heading) && (
            <>
              <div>
                <div className="bg-white p-3 rounded-3">
                  <div className="fw-bold small">YOUR CONTENT</div>
                  <hr className="my-2 text-secondary" />
                  <div
                    style={{ minHeight: 200, maxHeight: 400 }}
                    className="bg-white p-3 rounded-3 overflow-auto"
                  >
                    {Object.values(currentMessage).map((text, index) => (
                      <p key={index}>{text}</p>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <Tab.Container
                  activeKey={grammarStyle}
                  onSelect={(k) => setGrammarStyle(k)}
                  transition={false}
                  id="noanim-tab-example"
                  className="mb-3"
                >
                  <Nav variant="pills" className="mt-3 p-2 align-items-center">
                    <MyIcon Icon={AiIcon} width={25} />
                    <div className="ms-2 me-3">Show me </div>

                    <Nav.Item className="bg-purple-gradient">
                      <Nav.Link eventKey="formal" className="fw-bold">
                        FORMAL
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="informal" className="fw-bold">
                        INFORMAL
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="conversation" className="fw-bold">
                        CONVERSATIONAL FOCUSED
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                  <Tab.Content>
                    {/* <Tab.Pane eventKey="formal"> */}
                    <div className="bg-white p-3 rounded-3">
                      <div className=" small">
                        {loading ? (
                          <div
                            style={{ minHeight: 200 }}
                            className="d-flex justify-content-center align-items-center"
                          >
                            <FontAwesomeIcon icon={faSpinner} spin />
                          </div>
                        ) : (
                          correctGrammar &&
                          Object.entries(correctGrammar).map(
                            ([blockKey, block]) =>
                              Object.entries(block).map(
                                ([start, sentence], index) => (
                                  <div
                                    key={index}
                                    className="row mb-2 align-items-center"
                                  >
                                    <div className="col-auto">
                                      {sentence.original} =&gt;
                                    </div>
                                    <div className="col">
                                      {sentence.suggestions?.map(
                                        (suggestion, i) => (
                                          <div key={i}>
                                            <Form.Check
                                              type="checkbox"
                                              id={
                                                "replace" +
                                                blockKey +
                                                "-" +
                                                start +
                                                "-" +
                                                i
                                              }
                                              className="ms-2 d-inline-block align-baseline"
                                              label={suggestion}
                                              checked={
                                                replacements[blockKey] &&
                                                replacements[blockKey]?.[start]
                                                  ?.correct === suggestion
                                              }
                                              onChange={(e) => {
                                                setReplacements((prev) => ({
                                                  ...prev,
                                                  [blockKey]: {
                                                    ...(prev[blockKey] || {}),
                                                    [start]: e.target.checked
                                                      ? {
                                                          original:
                                                            sentence.original,
                                                          correct: suggestion,
                                                        }
                                                      : undefined,
                                                  },
                                                }));
                                              }}
                                            />
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )
                              )
                          )
                        )}
                      </div>
                    </div>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </>
          )}

          {["Language"].includes(aiType?.heading) && (
            <>
              <div>
                <div className="bg-white p-3 rounded-3">
                  <div className="fw-bold small">YOUR CONTENT</div>
                  <hr className="my-2 text-secondary" />
                  <div
                    style={{ minHeight: 200, maxHeight: 400 }}
                    className="bg-white p-3 rounded-3 overflow-auto"
                  >
                    {Object.values(currentMessage).map((text, index) => (
                      <p key={index}>{text}</p>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <Tab.Container
                  activeKey={grammarStyle}
                  onSelect={(k) => setGrammarStyle(k)}
                  transition={false}
                  id="noanim-tab-example"
                  className="mb-3"
                >
                  <Nav variant="pills" className="mt-3 p-2 align-items-center">
                    <MyIcon Icon={AiIcon} width={25} />
                    <div className="ms-2 me-3">Show me text in </div>
                    <TranslateIcon width={25} />

                    <Dropdown>
                      <Dropdown.Toggle
                        id="ai-suggestions-translate-dropdown"
                        className="d-flex align-items-center gap-3 bg-transparent border-0 text-secondary"
                      >
                        {translate.name && (
                          <div className="d-flex w-100">
                            <img
                              src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${translate.id}.svg`}
                              style={{ width: 15 }}
                              alt={`${translate.info} flag`}
                            />
                            <div className="ms-2">{translate.info}</div>
                          </div>
                        )}
                      </Dropdown.Toggle>

                      <Dropdown.Menu className="bg-purple-gradient">
                        <div className="d-flex align-items-center p-2 border-bottom">
                          <MyIcon Icon={AiIcon} width={25} className="me-2" />

                          <MyIcon
                            Icon={TranslateIcon}
                            width={15}
                            className="me-2"
                          />
                          <span className="text-navy fw-bold">boomAl: </span>
                          <span className="text-black">Translator</span>
                        </div>
                        {translateOptions.map((o) => (
                          <Dropdown.Item
                            // href="#/action-1"
                            className="border-bottom "
                          >
                            <button
                              type="button"
                              className={`btn p-1 w-100`}
                              onClick={(e) => {
                                e.preventDefault();
                                setTranslate(o);
                              }}
                            >
                              <div className="d-flex w-100">
                                {o.id && (
                                  <img
                                    src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${o.id}.svg`}
                                    style={{ width: 15 }}
                                    alt={`${o.info} flag`}
                                  />
                                )}
                                <div className="ms-2">{o.info}</div>
                              </div>{" "}
                            </button>
                          </Dropdown.Item>
                        ))}
                        <div className=" d-flex justify-content-between ms-2 px-2 text-xs align-items-center mt-2">
                          <Input
                            options={[
                              { value: "all", label: "ALL WORDS" },
                              { value: "selection", label: "SELECTION" },
                            ]}
                            onChange={(values) => setWords(values[0])}
                            value={[words]}
                            inputType="radio"
                          />
                          <Button
                            variant=""
                            className="ms-3 rounded-pill border-purple text-purple fw-bold border border-2 px-3 text-nowrap text-xs"
                          >
                            <FontAwesomeIcon icon={faCheck} /> APPLY{" "}
                          </Button>
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Nav>
                  <Tab.Content>
                    {/* <Tab.Pane eventKey="formal"> */}
                    <div className="bg-white p-3 rounded-3">
                      <div className=" small">
                        {loading ? (
                          <div
                            style={{ minHeight: 200 }}
                            className="d-flex justify-content-center align-items-center"
                          >
                            <FontAwesomeIcon icon={faSpinner} spin />
                          </div>
                        ) : (
                          translation &&
                          Object.entries(translation).map(
                            ([blockKey, block], index) => (
                              <div
                                key={index}
                                className="row mb-2 align-items-center"
                              >
                                <div className="col-auto">
                                  {block.original} =&gt;
                                </div>
                                <div className="col">
                                  {block.suggestions?.map((suggestion, i) => (
                                    <div key={i}>
                                      <Form.Check
                                        type="checkbox"
                                        id={"translate" + blockKey + i}
                                        className="ms-2 d-inline-block align-baseline"
                                        label={suggestion}
                                        checked={
                                          replacements[blockKey] &&
                                          replacements[blockKey]?.[0]
                                            ?.correct === suggestion
                                        }
                                        onChange={(e) => {
                                          setReplacements((prev) => ({
                                            ...prev,
                                            [blockKey]: {
                                              ...(prev[blockKey] || {}),
                                              [0]: e.target.checked
                                                ? {
                                                    original: block.original,
                                                    correct: suggestion,
                                                  }
                                                : undefined,
                                            },
                                          }));
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          )
                        )}
                      </div>
                    </div>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </>
          )}

          {aiType?.heading === "Form" && (
            <>
              <div>
                <div className="d-flex align-items-center my-3">
                  <div className="d-flex align-items-center">
                    <MyIcon Icon={AiIcon} width={25} />
                    <span className="ms-2 text-purple fw-bold">boomAI:</span>
                    <span className="ms-2">Form Recommendations</span>
                  </div>
                  <div className="ms-auto cursor-pointer" onClick={handleClose}>
                    <FontAwesomeIcon icon={faTimes} className="text-purple" />
                  </div>
                </div>
                <hr className="my-2 text-secondary" />
                <Row className="">
                  <Col className="border-end mx-2 my-3">
                    <div className="fw-bold text-navy">
                      You’ve selected the template: Engineer Visit
                    </div>
                    <div className="small my-3">
                      Due to the type of nature of an “Engineer Visit” using a
                      “2-Way” system, we recommend, lorem ipsum dolor sit amet,
                      consectetur adipiscing.
                    </div>
                  </Col>
                  <Col className="mx-2 my-3">
                    <div className="d-flex align-items-center bg-white p-3 rounded-3 ms-2">
                      <div className="bg-purple-gradient rounded-circle me-3">
                        <EngineerVisitIcon width={60} />
                      </div>
                      <div className="me-3">
                        <h3 className="text-navy fw-bold small">
                          Engineer Visit
                        </h3>
                        <div className="text-secondary small text-xxl-start text-break">
                          Lorem ipsum dolor sit amet consectetur adipisicing
                          elit. Corrupti ex sapiente
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
                <div className="d-flex align-items-center fw-bold my-3">
                  <MyIcon Icon={AiIcon} width={25} className="me-2" />
                  We recommend one of the following pre-made forms for this
                  scenario:
                  <HelpButton helpKey="ai-modal-engineer-visit" />
                </div>
                <div
                  style={{ maxHeight: 300, width: "100%", overflowY: "auto" }}
                >
                  <div
                    className="bg-white p-3 rounded-3 mx-2 my-3"
                    style={{
                      cursor: "pointer",
                      border: selected === 1 ? "2px solid #6c63ff" : "none",
                    }}
                  >
                    <Row
                      className="align-items-center "
                      onClick={() => setSelected(1)}
                    >
                      <div className="border-end col-5   ">
                        <div className="fw-bold">
                          2-Way XYZ lorem ipsum dolor sit amet,
                        </div>
                        <div className="small my-2">
                          Lorem ipsum dolor sit amet consectetur adipisicing
                          elit. Est blanditiis incidunt consequuntur soluta modi
                          beatae odio labore porro sunt esse ab quos
                          exercitationem nihil, tempora
                        </div>
                      </div>
                      <div className="col-5">
                        <div className="fw-bold">
                          This will setup the following :{" "}
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                      </div>
                      {selected === 1 && (
                        <div className="col-2 text-end">
                          <TickIcon className="me-2" width={15} />
                        </div>
                      )}
                    </Row>
                  </div>
                  <div
                    className="bg-white p-3 rounded-3 mx-2 my-3"
                    style={{
                      cursor: "pointer",
                      border: selected === 2 ? "2px solid #6c63ff" : "none",
                    }}
                  >
                    <Row
                      className="align-items-center"
                      onClick={() => setSelected(2)}
                    >
                      <div className="border-end col-5 ">
                        <div className="fw-bold">
                          Fully-featured XYZ lorem ipsum dolor sit amet,{" "}
                        </div>
                        <div className="small my-2">
                          Lorem ipsum dolor sit amet consectetur adipisicing
                          elit. Est blanditiis incidunt consequuntur soluta modi
                          beatae odio labore porro sunt esse ab quos
                          exercitationem nihil, tempora
                        </div>
                      </div>
                      <div className="col-5">
                        <div className="fw-bold">
                          This will setup the following :{" "}
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                      </div>
                      {selected === 2 && (
                        <div className="col-2 text-end">
                          <TickIcon className="me-2" width={15} />
                        </div>
                      )}
                    </Row>
                  </div>
                  <div
                    className="bg-white p-3 rounded-3 mx-2 my-3"
                    style={{
                      cursor: "pointer",
                      border: selected === 3 ? "2px solid #6c63ff" : "none",
                    }}
                  >
                    <Row
                      className="align-items-center"
                      onClick={() => setSelected(3)}
                    >
                      <div className="border-end col-5 ">
                        <div className="fw-bold">
                          1-Way XYZ lorem ipsum dolor sit amet,
                        </div>
                        <div className="small my-2">
                          Lorem ipsum dolor sit amet consectetur adipisicing
                          elit. Est blanditiis incidunt consequuntur soluta modi
                          beatae odio labore porro sunt esse ab quos
                          exercitationem nihil, tempora
                        </div>
                      </div>
                      <div className="col-5">
                        <div className="fw-bold">
                          This will setup the following :
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                        <div>
                          <TickIcon
                            className="me-2 bg-colored text-white rounded-pill"
                            width={15}
                          />{" "}
                          Lorem ipsum dolor sit amet, consectetur adipisicing
                        </div>
                      </div>
                      {selected === 3 && (
                        <div className="col-2 text-end">
                          <TickIcon className="me-2" width={15} />
                        </div>
                      )}
                    </Row>
                  </div>
                  <div className="d-flex justify-content-end mx-2">
                    <Button
                      variant=""
                      className="ms-3 rounded-pill border-purple bg-white text-purple fw-bold border border-2 px-3 text-nowrap text-xs"
                    >
                      <FontAwesomeIcon icon={faCheck} /> APPLY{" "}
                    </Button>
                  </div>
                </div>
              </div>
              <div>
                <div className="d-flex align-items-center my-3">
                  <div className="d-flex align-items-center">
                    <MyIcon Icon={AiIcon} width={25} />
                    <span className="ms-2 text-purple fw-bold">boomAI:</span>
                    <span className="ms-2">Survey Recommendations</span>
                  </div>
                  <div className="ms-auto cursor-pointer" onClick={handleClose}>
                    <FontAwesomeIcon icon={faTimes} className="text-purple" />
                  </div>
                </div>
                <hr className="my-2 text-secondary" />
                <Row className="">
                  <Col className="border-end mx-2 my-3">
                    <div className="fw-bold text-navy">
                      It looks like you havn’t added an XYZ action for this
                      survey you’re creating...{" "}
                    </div>
                    <div className="small my-3">
                      Due to the type of nature of an “Engineer Visit” using a
                      “2-Way” system, we recommend, lorem ipsum dolor sit amet,
                      consectetur adipiscing.
                    </div>
                  </Col>
                  <Col className="mx-2 my-3">
                    <div>
                      <TickIcon
                        className="me-2 bg-colored text-white rounded-pill"
                        width={15}
                      />{" "}
                      First and Last Name present.
                    </div>
                    <div>
                      <TickIcon
                        className="me-2 bg-colored text-white rounded-pill"
                        width={15}
                      />{" "}
                      Survey questions set.{" "}
                    </div>
                    <div>
                      <TickIcon
                        className="me-2 bg-colored text-white rounded-pill"
                        width={15}
                      />{" "}
                      Required responses set.
                    </div>
                    <div>
                      <TickIcon
                        className="me-2 bg-colored text-white rounded-pill"
                        width={15}
                      />
                      Opt-in message set.
                    </div>
                    <div className="fw-bold">
                      <CloseIcon
                        className="me-2 fw-bold bg-danger text-white rounded-pill"
                        width={15}
                      />{" "}
                      No reply Email address set.
                    </div>
                    <div className="fw-bold">
                      <CloseIcon
                        className="me-2 fw-bold bg-danger text-white rounded-pill"
                        width={15}
                      />
                      You havn’t selected any recipient groups.
                    </div>
                  </Col>
                </Row>
                <div className="d-flex align-items-center fw-bold  mx-2">
                  Recommended next steps..
                  <HelpButton helpKey="ai-modal-survey-recommendations" />
                </div>
                <div
                  style={{ maxHeight: 300, width: "100%", overflowY: "auto" }}
                >
                  <div
                    className="bg-white p-3 rounded-3 mx-2 my-3"
                    style={{
                      cursor: "pointer",
                      border: selected === 1 ? "2px solid #6c63ff" : "none",
                    }}
                  >
                    <Row
                      className="align-items-center "
                      onClick={() => setSelected(1)}
                    >
                      <div className=" col-7">
                        <div className="fw-bold">
                          2-Way XYZ lorem ipsum dolor sit amet,
                        </div>
                        <div className="small my-2">
                          Lorem ipsum dolor sit amet consectetur adipisicing
                          elit. Est blanditiis incidunt consequuntur soluta modi
                          beatae odio labore porro sunt esse ab quos
                          exercitationem nihil, tempora
                        </div>
                      </div>
                      {selected === 1 && (
                        <div className="col-5 text-end">
                          <TickIcon className="me-2" width={15} />
                        </div>
                      )}
                    </Row>
                  </div>
                  <div
                    className="bg-white p-3 rounded-3 mx-2 my-3"
                    style={{
                      cursor: "pointer",
                      border: selected === 2 ? "2px solid #6c63ff" : "none",
                    }}
                  >
                    <Row
                      className="align-items-center"
                      onClick={() => setSelected(2)}
                    >
                      <div className="col-7 ">
                        <div className="fw-bold">
                          Fully-featured XYZ lorem ipsum dolor sit amet,{" "}
                        </div>
                        <div className="small my-2">
                          Lorem ipsum dolor sit amet consectetur adipisicing
                          elit. Est blanditiis incidunt consequuntur soluta modi
                          beatae odio labore porro sunt esse ab quos
                          exercitationem nihil, tempora
                        </div>
                      </div>

                      {selected === 2 && (
                        <div className="col-5 text-end">
                          <TickIcon className="me-2" width={15} />
                        </div>
                      )}
                    </Row>
                  </div>
                  <div
                    className="bg-white p-3 rounded-3 mx-2 my-3"
                    style={{
                      cursor: "pointer",
                      border: selected === 3 ? "2px solid #6c63ff" : "none",
                    }}
                  >
                    <Row
                      className="align-items-center"
                      onClick={() => setSelected(3)}
                    >
                      <div className="col-7 ">
                        <div className="fw-bold">
                          1-Way XYZ lorem ipsum dolor sit amet,
                        </div>
                        <div className="small my-2">
                          Lorem ipsum dolor sit amet consectetur adipisicing
                          elit. Est blanditiis incidunt consequuntur soluta modi
                          beatae odio labore porro sunt esse ab quos
                          exercitationem nihil, tempora
                        </div>
                      </div>

                      {selected === 3 && (
                        <div className="col-5 text-end">
                          <TickIcon className="me-2" width={15} />
                        </div>
                      )}
                    </Row>
                  </div>
                  <div className="d-flex justify-content-end mx-2">
                    <Button
                      variant=""
                      className="ms-3 rounded-pill border-purple bg-white text-purple fw-bold border border-2 px-3 text-nowrap text-xs"
                    >
                      <FontAwesomeIcon icon={faCheck} /> APPLY{" "}
                    </Button>
                  </div>
                </div>
              </div>
              <div>
                <div className="d-flex align-items-center my-3">
                  <div className="d-flex align-items-center">
                    <MyIcon Icon={AiIcon} width={25} />
                    <span className="ms-2 text-purple fw-bold">boomAI:</span>
                    <span className="ms-2">Contact Field Recommendations</span>
                  </div>
                  <div className="ms-auto cursor-pointer" onClick={handleClose}>
                    <FontAwesomeIcon icon={faTimes} className="text-purple" />
                  </div>
                </div>
                <hr className="my-2 text-secondary" />
                <div className="d-flex">
                  <div className="mx-2 my-3 col-4 pe-3">
                    <div className="bg-icon d-flex flex-column p-2 rounded-3 align-items-center my-4">
                      <div className="mt-4">
                        {" "}
                        <GroupIcon Icon={AiIcon} width={50} />
                      </div>
                      <div className="fw-bold text-navy text-center fs-1 mt-3">
                        355
                      </div>
                      <div className="fw-bold text-center">Contacts Added</div>
                      <div className="fw-bold text-navy text-center fs-1 mt-3">
                        4
                      </div>
                      <div className="fw-bold text-center">
                        New fields discovered
                      </div>
                      <div className=" text-center small">
                        boomAI can automatically create these fields for you
                      </div>
                      <div className="text-center my-3">
                        <AiIcon width={25} className="" />{" "}
                        <HelpButton helpKey="ai-modal-contact-field-summary" />
                      </div>
                    </div>
                  </div>
                  <div className="col-8 pe-3 my-3 ">
                    <div className="">
                      <div className="d-flex mx-3 my-3 p-2">
                        <div className="me-3">
                          <MyIcon Icon={AiIcon} width={35} />
                        </div>
                        <div>
                          <div className="fw-bold text-navy">
                            I have created the following new fields to
                            accomodate.{" "}
                            <HelpButton helpKey="ai-modal-contact-field-recommendations" />{" "}
                          </div>
                          <div className="small">
                            I will automatically map, lorem ipsum dolor sit
                            amet, consectetur adipiscing elit. Nunc convallis
                            lacus et justo laoreet, eu dictum nisi porttitor.
                            Suspendisse sed luctus neque, ut auctor arcu
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between bg-white p-2 rounded-3 mx-3 my-2">
                        <div className="me-3">
                          <h3 className="text-navy fw-bold small">
                            Fax Number
                          </h3>
                        </div>
                        <div className="align-items-center d-flex">
                          <span className="bg-purple-gradient rounded-circle me-3">
                            <GroupIcon width={25} />
                          </span>
                          <span className="fw-bold me-3">
                            #CONTACTS
                            <br /> THAT USER
                          </span>
                          <span className="rounded-pill bg-purple-gradient fw-bold px-3 text-navy fs-3">
                            280
                          </span>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between bg-white p-2 rounded-3 mx-3 my-2">
                        <div className="me-3">
                          <h3 className="text-navy fw-bold small">
                            Favourite colour
                          </h3>
                        </div>
                        <div className="align-items-center d-flex">
                          <span className="bg-purple-gradient rounded-circle me-3">
                            <GroupIcon width={25} />
                          </span>
                          <span className="fw-bold me-3">
                            #CONTACTS
                            <br /> THAT USER
                          </span>
                          <span className="rounded-pill bg-purple-gradient fw-bold px-3 text-navy fs-3">
                            205
                          </span>
                        </div>
                      </div>{" "}
                      <div className="d-flex align-items-center justify-content-between bg-white p-2 rounded-3 mx-3 my-2">
                        <div className="me-3">
                          <h3 className="text-navy fw-bold small">Allergies</h3>
                        </div>
                        <div className="align-items-center d-flex">
                          <span className="bg-purple-gradient rounded-circle me-3">
                            <GroupIcon width={25} />
                          </span>
                          <span className="fw-bold me-3">
                            #CONTACTS
                            <br /> THAT USER
                          </span>
                          <span className="rounded-pill bg-purple-gradient fw-bold px-3 text-navy fs-3">
                            57
                          </span>
                        </div>
                      </div>{" "}
                      <div className="d-flex align-items-center justify-content-between bg-white p-2 rounded-3 mx-3 my-2">
                        <div className="me-3">
                          <h3 className="text-navy fw-bold small">
                            Mothers Maiden Name
                          </h3>
                        </div>
                        <div className="align-items-center d-flex">
                          <span className="bg-purple-gradient rounded-circle me-3">
                            <GroupIcon width={25} />
                          </span>
                          <span className="fw-bold me-3">
                            #CONTACTS
                            <br /> THAT USER
                          </span>
                          <span className="rounded-pill bg-purple-gradient fw-bold px-3 text-navy fs-3">
                            155
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mx-2 pe-3 my-4">
                      <Button
                        variant=""
                        className="ms-3 rounded-pill border-purple bg-purple-gradient-darker fw-bold border border-2 px-3 text-nowrap text-xs"
                      >
                        IGNORE
                      </Button>
                      <Button
                        variant=""
                        className="ms-3 rounded-pill border-purple bg-white text-purple fw-bold border border-2 px-3 text-nowrap text-xs"
                      >
                        <FontAwesomeIcon icon={faCheck} /> APPLY{" "}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="mt-4">
            {aiErrorMessage && (
              <Alert variant="secondary">{aiErrorMessage}</Alert>
            )}
            {/* <Form
              onSubmit={(e) => {
                e.preventDefault();
                getSuggestions(
                  aiType?.heading,
                  JSON.stringify(currentMessage),
                  grammarStyle,
                  translate,
                  aiPrompt
                );
              }}
            >
              <InputGroup>
                <InputGroup.Text>Message to AI</InputGroup.Text>
                <Form.Control
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                />
                <Form.Control
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                />
                <Button type="submit" variant="primary">
                  Send
                </Button>
              </InputGroup>
            </Form> */}
          </div>
          <div className="mt-3">
            <div className="d-flex justify-content-end">
              <MyIcon Icon={AiIcon} width={25} />

              <Button
                variant=""
                className="ms-3 rounded-pill border-purple text-purple fw-bold border border-2 px-3 text-nowrap text-xs"
                onClick={onReplace}
              >
                <FontAwesomeIcon icon={faCheck} /> APPLY
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
